// nuolino-admin/src/components/layout/Sidebar.tsx
// Sidebar desktop. Reusa primitives. Esconde em < md (lg+); MobileDrawer
// cuida de < md.

import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User } from 'lucide-react';
import { cn } from '@/lib/cn';
import { defaultSidebarSections, type SidebarSection } from './sidebar-sections';

export type { SidebarItem, SidebarSection } from './sidebar-sections';

export interface SidebarProps {
  sections?: SidebarSection[];
  onNavigate?: () => void;
  className?: string;
}

export function Sidebar({ sections = defaultSidebarSections, onNavigate, className }: SidebarProps) {
  const { t } = useTranslation();
  return (
    <aside
      className={cn('flex h-full w-64 flex-col border-r border-border-subtle bg-surface', className)}
      aria-label={t('common.menu')}
    >
      <div className="flex h-16 items-center border-b border-border-subtle px-5">
        <span className="text-lg font-semibold text-text">{t('app.name')}</span>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {sections.map((section, i) => (
          <div key={section.titleKey ?? `s-${i}`} className="mb-4">
            {section.titleKey && (
              <p className="mb-1 px-3 text-xs font-medium uppercase tracking-wide text-text-muted">{t(section.titleKey)}</p>
            )}
            <ul className="flex flex-col gap-0.5">
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        cn(
                          'group flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-primary-50 text-primary-700'
                            : 'text-text-muted hover:bg-surface-alt hover:text-text',
                        )
                      }
                    >
                      <span className="flex items-center gap-3">
                        <Icon className="h-4 w-4" aria-hidden="true" />
                        {t(item.labelKey)}
                      </span>
                      {item.badge !== undefined && item.badge > 0 && (
                        <span className="rounded-full bg-action px-2 py-0.5 text-xs font-semibold text-text-on-primary">
                          {item.badge}
                        </span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
      <div className="border-t border-border-subtle p-3">
        <NavLink
          to="/account"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              isActive ? 'bg-primary-50 text-primary-700' : 'text-text-muted hover:bg-surface-alt hover:text-text',
            )
          }
        >
          <User className="h-4 w-4" aria-hidden="true" />
          {t('common.account')}
        </NavLink>
      </div>
    </aside>
  );
}
